import React, { useState } from 'react';
import axios from 'axios';
import { Loader2, Sparkles, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { IITH, SERIF } from '@/pages/landing/iithBrand';

const API = process.env.REACT_APP_BACKEND_URL;

const SUGGESTIONS = [
  'Who is overloaded this period?',
  'Is OPD coverage adequate this week?',
  'Any schedule-drug risk I should review?',
  'Where should we add duty hours?',
];

/** Leadership Ask-AI grounded in selected-period telemetry. */
export default function ManagementAiSearch({ queryString }) {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [sources, setSources] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const ask = async (q) => {
    const text = (q || question).trim();
    if (!text) return;
    setQuestion(text);
    setLoading(true);
    setError('');
    setAnswer('');
    try {
      const params = new URLSearchParams(queryString || '');
      const { data } = await axios.post(
        `${API}/api/management/ai-ask`,
        {
          question: text,
          from: params.get('from') || undefined,
          to: params.get('to') || undefined,
          doctor_id: params.get('doctor_id') || undefined,
          specialisation: params.get('specialisation') || undefined,
        },
        { withCredentials: true },
      );
      setAnswer(data.answer || '');
      setSources(data.sources || null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not get an answer right now.');
    }
    setLoading(false);
  };

  return (
    <section
      className="rounded-2xl border overflow-hidden bg-white"
      data-testid="mgmt-ai-search"
    >
      <div
        className="px-5 py-4"
        style={{ background: `linear-gradient(120deg, ${IITH.navy} 0%, ${IITH.navyMid} 55%, #123556 100%)` }}
      >
        <p className="text-[11px] uppercase tracking-[0.2em]" style={{ color: IITH.gold }}>
          Ask AI · decision support
        </p>
        <h3 className="text-xl font-semibold text-white mt-0.5" style={{ fontFamily: SERIF }}>
          Ask about load, schedule & risk
        </h3>
        <p className="text-xs mt-1" style={{ color: IITH.mute }}>
          Prefers a local model (Ollama). Falls back to the on-device telemetry engine if offline.
        </p>
      </div>
      <div className="p-4 sm:p-5 space-y-3">
        <div className="flex gap-2">
          <input
            className="flex-1 h-11 border rounded-xl px-3 text-sm bg-white"
            placeholder="e.g. Who needs more roster cover this week?"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') ask(); }}
            data-testid="mgmt-ai-question"
          />
          <Button
            onClick={() => ask()}
            disabled={loading || !question.trim()}
            style={{ background: IITH.saffron }}
            className="text-white h-11 px-4"
            data-testid="mgmt-ai-ask"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => ask(s)}
              className="text-[11px] px-2.5 py-1 rounded-full border bg-zinc-50 hover:bg-zinc-100 text-zinc-700"
              data-testid="mgmt-ai-suggestion"
            >
              {s}
            </button>
          ))}
        </div>
        {error && <p className="text-sm text-rose-600">{error}</p>}
        {loading && (
          <p className="text-sm text-zinc-500 inline-flex items-center gap-2">
            <Sparkles className="h-4 w-4 animate-pulse" /> Analysing hospital telemetry…
          </p>
        )}
        {answer && (
          <div className="rounded-xl border bg-zinc-50 px-4 py-3 text-sm text-zinc-800 whitespace-pre-wrap leading-relaxed" data-testid="mgmt-ai-answer">
            {answer}
          </div>
        )}
        {sources && (
          <p className="text-[11px] text-zinc-500">
            Based on {sources.total_visits ?? 0} visits · {sources.doctors_active ?? 0} active doctors ·{' '}
            {sources.schedule_rx_lines ?? 0} schedule-Rx · {sources.roster_shifts ?? 0} roster shifts
            {sources.on_duty_today != null ? ` · ${sources.on_duty_today} on duty today` : ''}
          </p>
        )}
      </div>
    </section>
  );
}
